import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';

import HaspObject from './HaspObject';
import CustomArc from '../draw/CustomArc';
import { toWireKey } from './properties/v2/wireFormat.js';
import { resolvePadding, resolveRadialPadding, onPaddingStyle } from './properties/v2/padding.js';
import { processIconText } from '../utils/iconFonts';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';

// speed	    int16	1000	The time for 1 turn in ms higher is slower
// direction	int16	0	    0 = clockwise, 1 = counter-clockwise
// angle	    0-360	60	    The length of the spinning segment in degrees
// type	        0-2	    0	    0 = slow down on the top, 1 = slow down and stretch, 2 = constant speed no stretch
// main part: line_width/line_color style the background circle
// indicator part: line_width/line_color style the spinning segment

const SPIN_MODE_SLOW = 0;
const SPIN_MODE_STRETCH = 1;
const SPIN_MODE_CONSTANT = 2;

const DEFAULT_LINE_WIDTH = 20;

export default class HaspSpinner extends HaspObject {
    // type = SPIN_MODE_SLOW;
    hasp_type_options = [
        { value: SPIN_MODE_SLOW, description: "slow down" },
        { value: SPIN_MODE_STRETCH, description: "slow stretch" },
        { value: SPIN_MODE_CONSTANT, description: "constant" }
    ]
    anim;

    constructor(config) {
        super(config);

        this._valueFontBinding = new FontBinding((family) => {
            this.valueText.fontFamily(family);
            this.valueText.getLayer()?.batchDraw();
        });

        const halfWidth = this.width() / 2;
        const mainStyle = this.style('main');
        const indicatorStyle = this.style('indicator');
        // Effective per-side padding — an asymmetric pad shifts the centre and
        // shrinks the radius (see resolveRadialPadding); a uniform one behaves
        // exactly as the old scalar `padding` did.
        const { offsetX, offsetY, inset: pad } = resolveRadialPadding(mainStyle);

        this.spin = new CustomArc({
            id: uuidv4(),
            x: halfWidth + offsetX,
            y: halfWidth + offsetY,
            obj: "spinner",
            outerRadius: halfWidth - pad,
            innerRadius: halfWidth - pad - indicatorStyle.line_width,
            startAngle: 0,
            endAngle: this.hasp_angle,
            fill: indicatorStyle.line_color,
            strokeWidth: 0,
        })
        this.ring = new Konva.Ring({
            id: uuidv4(),
            x: halfWidth + offsetX,
            y: halfWidth + offsetY,
            obj: "ring",
            outerRadius: halfWidth - pad,
            innerRadius: halfWidth - pad - mainStyle.line_width,
            fill: mainStyle.line_color,
            // stroke: "black"
            strokeWidth: 0,
        })

        // this.rect.opacity(0)
        // this.setFill('rgba(0,255,0,0.0');

        this.on('transform', function () {
            this.repositionArc();
            this.textPosition();
        });

        this.on('transformend', (e) => {
            this.repositionArc();
            this.textPosition();
        });
        this.on('angle', (e) => { this.spin.angle(this.hasp_angle); this.repositionArc() })
        this.on('speed', (e) => { this.repositionArc() })
        this.onStyle('main', 'line_width', () => { this.repositionArc() })
        this.onStyle('indicator', 'line_width', () => { this.repositionArc() })
        // Shorthand + all four per-side overrides, one subscription helper.
        onPaddingStyle(this, 'main', () => { this.repositionArc() })
        this.onStyle('main', 'line_color', () => { this.ring.fill(this.style('main').line_color) })
        this.onStyle('indicator', 'line_color', () => {
            const { line_color } = this.style('indicator');
            this.spin.fill(line_color);
            this.spin.stroke(line_color)
        });
        this.on('type', (e) => {
            // console.log('type', this.type);
        });
        this.on('direction', (e) => {
            // console.log('direction', this.direction);
        });
        this.onStyle('indicator', 'line_rounded', () => {
            let cap = 'square';
            if (this.style('indicator').line_rounded) {
                cap = 'round';
            }
            this.spin.lineRounded(cap);
        })

        this.on('value_color', (e) => {
            this.valueText.fill(this.value_color);
        })

        this.on('value_str', (e) => {
            this.valueText.text(processIconText(this.value_str));
        });

        this.on('value_font', (e) => {
            this.valueText.fontSize(this.value_font);
        });

        this.on('value_font_resource', () => {
            this._applyValueFont();
        });

        var spin = this.spin;
        this.anim = new Konva.Animation((frame) => {
            // Base angular speed is determined by the speed property
            const rotationSpeed = 360 / this.speed; // Degrees per millisecond

            // Determine the rotation direction
            const directionMultiplier = this.direction ? -1 : 1; // -1 for counterclockwise, 1 for clockwise

            // Scale the angle between 45 and 270 degrees
            const minAngle = 45;
            const maxAngle = 270;
            const cycleDuration = this.speed; // Use the speed property for one full cycle
            const progress = (frame.time % cycleDuration) / cycleDuration; // Progress of the current cycle (0 to 1)

            if (this.type === SPIN_MODE_CONSTANT) {
                // Rotate the arc at a constant speed
                const angleDiff = (frame.timeDiff * rotationSpeed) * directionMultiplier;
                spin.rotate(angleDiff);
            } else if (this.type === SPIN_MODE_STRETCH) {
                // Use a sinusoidal function to smoothly stretch and shrink
                const scaledAngle = minAngle + (maxAngle - minAngle) * (0.5 - 0.5 * Math.cos(2 * Math.PI * progress));
                spin.startAngle(0); // Always start at 0
                spin.endAngle(scaledAngle); // Dynamically adjust the end angle

                // Adjust rotation speed based on the shrinking/stretching progress
                const speedMultiplier = 1 + (1 - (scaledAngle - minAngle) / (maxAngle - minAngle)); // Faster when shrinking
                const adjustedRotationSpeed = rotationSpeed * speedMultiplier;

                // Rotate the arc
                const angleDiff = (frame.timeDiff * adjustedRotationSpeed) * directionMultiplier;
                spin.rotate(angleDiff);
            } else if (this.type === SPIN_MODE_SLOW) {
                // Slow down at the top (12 o'clock position, 270 degrees)
                const slowDownFactor = 0.5 - 0.5 * Math.cos(2 * Math.PI * (progress - 0.75)); // Shift by 0.75 to align with 270 degrees
                const adjustedRotationSpeed = rotationSpeed * (0.5 + slowDownFactor); // Modulate speed (0.5 to 1.5)
                const angleDiff = (frame.timeDiff * adjustedRotationSpeed) * directionMultiplier;

                // Rotate the arc
                spin.rotate(angleDiff);
            }
        }, this.getLayer());


        this.add(this.ring);
        this.add(this.spin);
        this.createText();
        this._applyValueFont();
        this.draggable(true);
        this.anim.start();
    };

    destroy() {
        this._valueFontBinding.dispose();
        if (this.anim)
            this.anim.stop();
        super.destroy();
    };

    /**
     * The uploaded Font Resource `this.value_font_resource` resolves to for
     * canvas preview — see HaspGauge.js's identically-named helper for the
     * full semantics.
     */
    _valueFontResource() {
        return this.value_font_resource ? useFontStore().fonts.find(f => f.file === this.value_font_resource) : null;
    }

    /**
     * Resolve which font family the value text (this.valueText) should
     * render with. Falls back to 'Material Icons' (the pre-existing
     * hardcoded default) when no Font Resource is selected.
     */
    _applyValueFont() {
        this._valueFontBinding.set(this._valueFontResource(), 'Material Icons');
    }

    transformStart() {
        // this.setAnimation(false);
        // this.ring.visible(false);
        // this.spin.visible(false);
    };

    transformEnd() {
        // this.ring.visible(true);
        // this.spin.visible(true);
        // this.setAnimation(true);
    };

    redraw(args) {
        this.spin.fill(this.style('indicator').line_color);
        this.ring.fill(this.style('main').line_color);
    }

    setPadding(padding) {
        const pad = Number(this.style('main').padding);
        if (pad < (this.width() / 4) && pad > 0) {
            this.setStyle('main', 'padding', pad);
            this.repositionArc()
        }
    }

    setAnimation(event) {
        this.animation = Boolean(event);
        if (this.anim) {
            if (this.animation)
                this.anim.start();
            else
                this.anim.stop();
        }
    }

    setNewSize(size) {
        super.setNewSize(size);
        this.repositionArc();
        this.textPosition();
    }

    createText() {
        this.valueText = new Konva.Text({
            x: 0,
            verticalAlign: 'middle',
            align: 'center',
            text: processIconText(this.value_str),
            fontSize: this.value_font,
            fontFamily: 'Material Icons',
            fill: this.value_color,
            listening: true,
            draggable: true,
            strokeWidth: 1,
            obj: 'value_text',
        });

        this.add(this.valueText);
        this.valueText.x((this.width() / 2) - (this.valueText.width() / 2) + (this.value_ofs_x || 0));
        this.valueText.y((this.height() / 2) - (this.valueText.height() / 2) + (this.value_ofs_y || 0));

        this.valueText.on('dragstart', (e) => {
            this.textBounds();
        });
        this.valueText.on('dragmove', (e) => {
            this.textBounds();
        });
        this.valueText.on('dragend', (e) => {
            this.textBounds();
        });
    }

    textBounds() {
        if (this.valueText !== undefined) {
            if (this.valueText.x() < 0) this.valueText.x(0);
            if (this.valueText.x() > this.width() - this.valueText.width()) this.valueText.x(this.width() - this.valueText.width());
            if (this.valueText.y() < 0) this.valueText.y(0);
            if (this.valueText.y() > this.height() - this.valueText.height()) this.valueText.y(this.height() - this.valueText.height());
        }
    }

    textPosition() {
        if (this.valueText !== undefined) {
            var scaledWidth = this.valueText.x() * this.scaledX;
            this.valueText.x(scaledWidth);
            const scaledHeight = this.valueText.y() * this.scaledY;
            this.valueText.y(scaledHeight);
            this.textBounds();
        }
    }

    repositionArc() {
        if (!this.spin) {
            return;
        }
        const halfWidth = (this.rect.width() / 2);
        const main = this.style('main');
        const indicator = this.style('indicator');
        const { offsetX, offsetY, inset: padding } = resolveRadialPadding(main);

        let lineWidth = main.line_width;
        let indicatorLineWidth = indicator.line_width;
        if (lineWidth > halfWidth) {
            lineWidth = halfWidth;
            this.pokeStyle('main', 'line_width', lineWidth);
        }
        if (indicatorLineWidth > halfWidth) {
            indicatorLineWidth = halfWidth;
            this.pokeStyle('indicator', 'line_width', indicatorLineWidth);
        }

        this.spin.x(halfWidth + offsetX);
        this.spin.y(halfWidth + offsetY)
        this.spin.outerRadius(halfWidth - padding)
        this.spin.innerRadius(halfWidth - padding - Number(indicatorLineWidth))

        this.ring.x(halfWidth + offsetX);
        this.ring.y(halfWidth + offsetY)
        this.ring.outerRadius(halfWidth - padding)
        this.ring.innerRadius(halfWidth - padding - Number(lineWidth))
    }

    // themeChange(type, value) {
    //     if (type === 'primary_color') {
    //         this.spin.fill(this.theme.primary_color)
    //     }
    // }

    objectExport(page, objectData, hassConfig) {
        const main = this.style('main');
        const indicator = this.style('indicator');

        // Effective per-side padding (side ?? padding ?? 0 — see v2/padding.js).
        // `??=` so neither the generic style loop's explicit side overrides nor
        // ObjectExport's own shorthand expansion get overwritten here.
        const pad = resolvePadding(main);
        if (pad.top > 0 || pad.right > 0 || pad.bottom > 0 || pad.left > 0) {
            objectData.pad_top ??= pad.top;
            objectData.pad_right ??= pad.right;
            objectData.pad_left ??= pad.left;
            objectData.pad_bottom ??= pad.bottom;
        }

        if (this.speed != this.defaults.speed) { objectData.speed = this.speed; }
        if (this.hasp_angle !== 90) { objectData.angle = this.hasp_angle };
        if (this.type > 0) { objectData.type = this.type; }
        if (main.line_width !== DEFAULT_LINE_WIDTH) { objectData.line_width = main.line_width; }
        if (indicator.line_width !== DEFAULT_LINE_WIDTH) {
            const wireKey = toWireKey('indicator', 'line_width', this.getStyleDefinition('indicator'));
            objectData[wireKey] = indicator.line_width;
        }
        if (this.value_str !== '') {
            objectData.value_ofs_x = this.valueText.x() + (this.valueText.width() / 2) - (this.width() / 2);
            objectData.value_ofs_y = this.valueText.y() + (this.valueText.height() / 2) - (this.height() / 2);
        }
    }

}